import { useState } from "react";
import { waysIn, type CatalogConnector } from "../../../shared/connector-catalog";
import type { ConnectorDTO } from "../../../shared/types";
import { accessCharacter, DEFAULT_ACCESS_LEVEL, hasWorkspaceKey, type WorkspaceMode } from "../../../shared/types";
import { confirmDialog } from "../../components/Dialog";
import { List, ListRow } from "../../components/List";
import { PolicyEditor, policySummary } from "../../components/PolicyEditor";
import { TextLink } from "../../components/TextLink";
import { Badge, Button, Choice, ErrorText, Field, inputCls, Json, Panel } from "../../components/ui";
import { del, patch, post } from "../../lib/api";
import { CopyBox, type ConnectorAccess } from "./ConnectorWays";

type TestResult = { status: number | string; ok?: boolean; body?: string; error?: string };

/** A tool's key fields, as its catalog entry names them. */
export function SecretInputs({ fields, values, onChange }: { fields: CatalogConnector["secretFields"]; values: Record<string, string>; onChange: (v: Record<string, string>) => void }) {
  return (
    <>
      {fields.map((f) => (
        <Field key={f.key} label={f.label} hint={f.help}>
          {f.multiline ? (
            <textarea
              rows={6}
              autoComplete="off"
              spellCheck={false}
              className={`${inputCls} font-mono text-xs`}
              placeholder={f.placeholder}
              value={values[f.key] ?? ""}
              onChange={(e) => onChange({ ...values, [f.key]: e.target.value })}
            />
          ) : (
            <input
              type="password"
              autoComplete="off"
              className={`${inputCls} font-mono`}
              placeholder={f.placeholder}
              value={values[f.key] ?? ""}
              onChange={(e) => onChange({ ...values, [f.key]: e.target.value })}
            />
          )}
        </Field>
      ))}
    </>
  );
}

/** Google Workspace: what to authorize in the Google Admin console for domain-wide delegation. */
function DelegationSetup({ connector }: { connector: ConnectorDTO }) {
  const clientId = typeof connector.authConfig.sa_client_id === "string" ? connector.authConfig.sa_client_id : null;
  const scopes = Array.isArray(connector.authConfig.scopes) ? (connector.authConfig.scopes as string[]) : [];
  return (
    <div className="space-y-3 text-sm">
      <h3 className="font-semibold">Authorize in the Google Admin console</h3>
      <ol className="list-decimal space-y-1 pl-5 text-muted">
        <li>
          Open{" "}
          <TextLink href="https://admin.google.com/ac/owl/domainwidedelegation">admin.google.com/ac/owl/domainwidedelegation</TextLink>{" "}
          (Security → Access and data control → API controls → Manage Domain Wide Delegation).
        </li>
        <li>Click <b>Add new</b>, paste the Client ID and the scopes below, and save.</li>
        <li>Run the test below. Agents then act as whoever asks — their own Drive, Calendar and Gmail.</li>
      </ol>
      {clientId ? <CopyBox label="Client ID" value={clientId} /> : <p className="text-warning">Save the service account key to see its Client ID.</p>}
      <CopyBox label="Scopes" value={scopes.join(",\n")} />
      {typeof connector.authConfig.sa_client_email === "string" && <p className="text-xs text-muted">Service account: {connector.authConfig.sa_client_email}</p>}
    </div>
  );
}

/** "Test the key": a GET with the workspace key, also on a connector whose people use their own logins. */
function TestPanel({ connector, onTested }: { connector: ConnectorDTO; onTested?: () => void }) {
  const [path, setPath] = useState(String(connector.authConfig.test_path ?? "/"));
  // People use their own login here, so the workspace key serves agents alone and answers only its own test path.
  const fixed = connector.credentials === "own";
  const [result, setResult] = useState<TestResult | null>(null);
  return (
    <div className="space-y-2">
      <Field label="Test the key (GET)">
        <div className="flex gap-2">
          <input className={`${inputCls} font-mono`} value={fixed ? String(connector.authConfig.test_path ?? "/") : path} readOnly={fixed} onChange={(e) => setPath(e.target.value)} />
          <Button
            onClick={async () => {
              setResult({ status: "…" });
              try {
                setResult(await post<TestResult>(`/api/connectors/${connector.id}/test`, { path }));
                onTested?.();
              } catch (err) {
                setResult({ status: "error", error: (err as Error).message });
              }
            }}
          >
            Test
          </Button>
        </div>
      </Field>
      {result && (
        <div className="space-y-1">
          <div className="text-sm">
            {result.ok ? <Badge tone="emerald">HTTP {result.status} · working</Badge> : <Badge tone={result.status === "…" ? "zinc" : "red"}>{result.status === "…" ? "testing…" : `HTTP ${result.status}`}</Badge>}
          </div>
          {(result.body || result.error) && <Json value={result.error ?? result.body} className="max-h-48 whitespace-pre-wrap" />}
        </div>
      )}
    </div>
  );
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The workspace key: whether one is stored (optional where people have their own login), who uses it, its test, its
 * replacement and its removal — or, where the tool declares it, the sign-in app itself standing as one (AIT-57).
 */
export function WorkspaceKey({ connector, entry, access, onSaved, busy, run }: {
  connector: ConnectorDTO;
  entry: CatalogConnector | undefined;
  access: ConnectorAccess | null;
  onSaved: () => Promise<void> | void;
  busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [secretsJson, setSecretsJson] = useState("");
  const fields = entry?.secretFields;
  const ways = waysIn(connector);
  const own = connector.credentials === "own";
  const optional = Boolean(ways.signIn || ways.token);
  const agents = access?.agents.length ?? 0;
  const viaApp = connector.workspaceMode === "signin_app";
  const hasKey = hasWorkspaceKey(connector);
  // The sign-in app itself may stand as the key where the tool declares it, people use their own login and the app is set up.
  const offerApp = Boolean(ways.app) && own && (connector.signInReady || viaApp);
  const name = connector.name;
  const who = !access
    ? null
    : connector.authType === "google_service_account"
      ? "Agents act through it as the person asking."
      : viaApp
        ? `Used by agents working on their own (${count(agents, "agent", "agents")}), which read ${name} as the sign-in app itself: no person, read only. People's runs use their own login.`
      : !connector.hasSecrets
        ? own
          ? `Without one, agents working on their own cannot use ${connector.name} (${count(agents, "agent has", "agents have")} it). People's runs use their own login.`
          : `Without one, nobody can use ${connector.name}.`
        : own
          ? `Used by agents working on their own (${count(agents, "agent", "agents")}). People's runs use their own login.`
          : `Used by ${count(access.people.length, "person", "people")} and ${count(agents, "agent", "agents")} working on their own, each at their level.`;
  const switchTo = (mode: WorkspaceMode) =>
    run(async () => {
      const sure = await confirmDialog(mode === "signin_app"
        ? {
            title: `Use ${name}'s sign-in app as the workspace key?`,
            body: `AgentOS first asks ${name} for the app's own token, read only. ${connector.hasSecrets ? "Only once it has one is the stored key removed, for good. " : ""}Agents working on their own then read ${name} as the app, no person. People's own logins and the sign-in app stay as they are.`,
            confirm: "Use the sign-in app",
            danger: connector.hasSecrets,
          }
        : { title: "Back to a key you paste?", body: `The sign-in app's token is dropped. Until a key is stored, agents working on their own cannot use ${name}.`, confirm: "Switch" });
      if (!sure) return;
      await patch(`/api/connectors/${connector.id}`, { workspaceMode: mode });
      await onSaved();
    });
  const remove = () =>
    run(async () => {
      const sure = await confirmDialog({
        title: `Remove ${name}'s workspace key?`,
        body: `${viaApp ? "The sign-in app stops standing as the key; it stays for people's own sign-in." : "The stored key is deleted for good."} Agents working on their own cannot use ${name} until a key is stored. People keep their own login.`,
        confirm: "Remove key",
        danger: true,
      });
      if (!sure) return;
      await del(`/api/connectors/${connector.id}/secrets`);
      await onSaved();
    });
  return (
    <Panel
      title={
        <span className="flex flex-wrap items-center gap-2">
          Workspace key
          {viaApp ? <Badge tone="emerald">sign-in app</Badge> : connector.hasSecrets ? <Badge tone="emerald">stored</Badge> : <Badge tone={own ? "zinc" : "amber"}>none</Badge>}
          {connector.needsReconnect && <Badge tone="red">{viaApp ? "app token refused" : "reconnect"}</Badge>}
        </span>
      }
      hint={optional ? `Optional for ${connector.name}: people can use their own login, and the key serves agents working on their own.` : `One key for the whole workspace, stored encrypted and never shown again.`}
    >
      <div className="space-y-4">
        {who && <p className={`text-sm ${!connector.hasSecrets && !own ? "text-warning" : ""}`}>{who}</p>}
        {offerApp && (
          <div className="space-y-2">
            <Choice<WorkspaceMode>
              label="Workspace key"
              value={connector.workspaceMode}
              disabled={busy}
              onChange={(mode) => mode !== connector.workspaceMode && void switchTo(mode)}
              options={[{ value: "key", label: "A key you paste" }, { value: "signin_app", label: "The sign-in app itself (no person)" }]}
            />
            <p className="text-xs text-muted">
              {viaApp
                ? `AgentOS renews the app's token a day before it lapses${connector.workspaceTokenExpiresAt ? ` (this one lapses ${new Date(connector.workspaceTokenExpiresAt).toLocaleDateString()})` : ""}.`
                : ways.app!.steps}
            </p>
          </div>
        )}
        {viaApp && connector.needsReconnect && (
          <ErrorText error={`${name}'s app token was refused. Check that the sign-in app is set up and its Client credentials is on in ${name}, then Test the key: a token that gets through clears this.`} />
        )}
        {connector.authType === "oauth2" && (
          <div className="space-y-2 text-sm">
            <p className="text-muted">
              Redirect URI to register with the provider: <code className="break-all">{`${location.origin}/oauth/callback/${connector.slug}`}</code>
            </p>
            <Button
              variant="primary"
              onClick={() => run(async () => { location.href = (await post<{ url: string }>(`/api/connectors/${connector.id}/oauth/start`)).url; })}
            >
              {connector.needsReconnect ? "Reconnect" : "Connect"} with {connector.name}
            </Button>
          </div>
        )}
        {connector.authType === "google_service_account" && <DelegationSetup connector={connector} />}
        {hasKey && <TestPanel connector={connector} onTested={() => void onSaved()} />}
        {own && hasKey && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-4">
            <p className="text-sm text-muted">People use their own login, so the workspace key can go: agents working on their own then cannot use {name}.</p>
            <Button size="sm" variant="danger" disabled={busy} onClick={() => void remove()}>Remove</Button>
          </div>
        )}
        {!viaApp && <div className="space-y-3 border-t border-line pt-4">
          <h4 className="text-sm font-medium">{connector.hasSecrets ? "Replace the key" : "Store a key"}</h4>
          {fields ? (
            <SecretInputs fields={fields} values={values} onChange={setValues} />
          ) : (
            <Field label="Secrets (JSON)" hint="bearer: token · header: api_key · oauth2: client_id, client_secret">
              <textarea className={`${inputCls} font-mono text-xs`} rows={3} value={secretsJson} onChange={(e) => setSecretsJson(e.target.value)} placeholder={connector.hasSecrets ? "•••••• stored" : ""} />
            </Field>
          )}
          <Button
            size="sm"
            disabled={busy || (fields ? fields.some((f) => !f.optional && !values[f.key]?.trim()) : !secretsJson.trim())}
            onClick={() =>
              run(async () => {
                const secrets = fields
                  ? Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v))
                  : JSON.parse(secretsJson);
                await patch(`/api/connectors/${connector.id}`, { secrets });
                setValues({});
                setSecretsJson("");
                await onSaved();
              })
            }
          >
            {connector.hasSecrets ? "Save new key" : "Store the key"}
          </Button>
        </div>}
      </div>
    </Panel>
  );
}

/** The workspace key's levels: the default every new grant gets, and the custom ones, each opened in the levels editor. */
export function AccessLevels({ connector, entry, onSaved }: { connector: ConnectorDTO; entry: CatalogConnector | undefined; onSaved: () => Promise<void> | void }) {
  const [editing, setEditing] = useState<string | null>(null);
  const own = connector.credentials === "own";
  // On the workspace key a level's rules replace the default's, so one that leaves a block or a hidden field out is named.
  const { deny = [], redactFields: hidden = [] } = connector.policy;
  const gaps = connector.levels.filter((l) => deny.some((r) => !(l.policy.deny ?? []).includes(r)) || hidden.some((f) => !(l.policy.redactFields ?? []).includes(f)));
  const rows = [
    { key: DEFAULT_ACCESS_LEVEL, name: "Default", policy: connector.policy, auto: false },
    ...connector.levels.map((l) => ({ key: l.key, name: l.name, policy: l.policy, auto: Boolean(l.autoAssignAdmins) })),
  ];
  return (
    <div className="space-y-3">
      <List
        title="Access levels"
        hint={connector.credentials === "internal"
          ? `What each person and agent may do in ${connector.name} through AgentOS, within what ${connector.name} itself lets them. New grants get the default level.`
          : `What ${own ? "agents working on their own" : "each person and agent"} may do with the workspace key. New grants get the default level.`}
        // While the editor is open it is the one place levels change: its own tabs move between them, so no draft is dropped.
        action={!editing && <Button size="sm" onClick={() => setEditing("new")}>Add level</Button>}
      >
        {rows.map((r) => {
          const rules = policySummary({ ...r.policy, protected: undefined });
          return (
            <ListRow
              key={r.key}
              title={
                <>
                  <span className="truncate text-sm font-medium">{r.name}</span>
                  {r.key === DEFAULT_ACCESS_LEVEL && <Badge>new grants</Badge>}
                  {r.auto && <Badge tone="blue">admins get it</Badge>}
                </>
              }
              subtitle={<span className="text-xs">{accessCharacter(r.policy)}{rules ? ` · ${rules}` : ""}</span>}
              trailing={!editing && <Button size="sm" variant="ghost" onClick={() => setEditing(r.key)}>Edit</Button>}
            />
          );
        })}
      </List>
      {gaps.length > 0 && (
        <p className="text-xs text-warning">
          {connector.credentials === "internal" ? "Here" : "On the workspace key"}, the {gaps.map((l) => l.name).join(", ")} level{gaps.length === 1 ? " lists its own blocked endpoints and hidden fields and leaves some of the default's out" : "s list their own blocked endpoints and hidden fields and leave some of the default's out"}.
        </p>
      )}
      {editing && (
        <div className="card space-y-3 p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">{editing === "new" ? "New level" : "Edit levels"}</h3>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Done</Button>
          </div>
          <PolicyEditor key={`${connector.id}-${editing}`} connector={connector} entry={entry} onSaved={onSaved} startAt={editing} embedded />
        </div>
      )}
    </div>
  );
}
